import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { AuthLayout } from '../components/AppShell.js';
import { Alert, Button, TextField } from '../components/ui.js';
import { authClient } from '../lib/authClient.js';

const MIN_PASSWORD_LENGTH = 10;

export function ResetPasswordPage(): React.JSX.Element {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Please use at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (!token) {
      setError('That reset link is not valid. Please request a new one.');
      return;
    }

    setBusy(true);
    try {
      const result = await authClient.resetPassword({ newPassword: password, token });
      if (result.error) {
        setError('That reset link has expired or has already been used.');
        return;
      }
      void navigate('/sign-in', { replace: true });
    } catch {
      setError('We could not reach the server. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title="Choose a new password"
      footer={
        <Link to="/sign-in" className="font-medium text-fg underline">
          Back to sign in
        </Link>
      }
    >
      {token ? (
        <form onSubmit={(event) => void handleSubmit(event)} className="space-y-5" noValidate>
          {error ? <Alert>{error}</Alert> : null}

          <TextField
            label="New password"
            hint={`At least ${MIN_PASSWORD_LENGTH} characters.`}
            type="password"
            name="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />

          <Button type="submit" busy={busy} className="w-full">
            Save new password
          </Button>
        </form>
      ) : (
        <Alert>
          That reset link is not valid. Please{' '}
          <Link to="/forgot-password" className="underline">
            request a new one
          </Link>
          .
        </Alert>
      )}
    </AuthLayout>
  );
}
